import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingDown } from 'lucide-react';

const serif = { fontFamily: '"Playfair Display", Georgia, serif' };

/**
 * RichPriceComparison
 * ===================
 * Renders the REAL market price comparison from pricing_data.topCompetitors
 * (sourced from Strømligning.dk). This is legitimate market data — it ranks
 * enkel against other suppliers for a given consumption, with full source
 * attribution. Used by city/region/best-list rich templates.
 *
 * Props: enkelCost, consumption, totalCompanies, topCompetitors[], timestamp, place
 */
export default function RichPriceComparison({
  enkelCost = 216,
  consumption = 9000,
  totalCompanies = 50,
  topCompetitors = [],
  timestamp,
  place = '',
}) {
  const comps = (topCompetitors || []).filter((c) => c && c.yearlyCost > 0).slice(0, 8);
  const maxCost = Math.max(enkelCost, ...comps.map((c) => c.yearlyCost), enkelCost + 500);
  const fmt = (n) => Math.round(n).toLocaleString('da-DK');
  const dateStr = timestamp
    ? new Date(timestamp).toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' })
    : 'i dag';

  const Bar = ({ label, value, isEnkel, savings }) => (
    <div>
      <div className="flex items-center justify-between mb-1 text-sm">
        <span className={isEnkel ? 'font-bold text-[#1F2E33]' : 'text-gray-700'}>{label}</span>
        <span className={isEnkel ? 'font-bold text-[#1F2E33]' : 'text-gray-600'}>{fmt(value)} kr./år</span>
      </div>
      <div className="w-full h-7 rounded-lg bg-gray-100 overflow-hidden relative">
        <div
          className={`h-full rounded-lg flex items-center justify-end pr-2 ${isEnkel ? 'bg-[#D4A62A]' : 'bg-[#5E7B8C]'}`}
          style={{ width: `${Math.max(8, (value / maxCost) * 100)}%` }}
        >
          {isEnkel && <span className="text-[10px] font-bold text-[#1F2E33]">Nr. 1</span>}
        </div>
        {!isEnkel && savings > 0 && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-[#b23b30]">
            +{fmt(savings)} kr.
          </span>
        )}
      </div>
    </div>
  );

  return (
    <section className="py-12 md:py-16 bg-white" id="priser">
      <div className="container mx-auto px-6">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-[#D4A62A]/15 text-[#a07e1c] rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide mb-3">
            <TrendingDown className="w-3.5 h-3.5" /> Live prissammenligning
          </div>
          <h2 className="text-2xl md:text-3xl font-bold mb-2 text-[#1F2E33]" style={serif}>
            Prissammenligning{place}
          </h2>
          <p className="text-gray-600 mb-8">
            Årlig selskabsomkostning ved {fmt(consumption)} kWh/år. Data fra den uvildige tjeneste Strømligning.dk.
          </p>

          {comps.length > 0 ? (
            <>
              <div className="bg-[#F6F3EC] rounded-2xl p-5 md:p-6 border border-[#263A43]/10 space-y-4 mb-4">
                <Bar label="enkel energi (0 øre tillæg)" value={enkelCost} isEnkel />
                {comps.map((c, i) => (
                  <Bar key={i} label={c.name} value={c.yearlyCost} savings={c.savings} />
                ))}
              </div>

              <div className="overflow-x-auto rounded-2xl border border-[#263A43]/12 shadow-sm">
                <table className="w-full text-sm bg-white">
                  <thead>
                    <tr className="bg-[#1F2E33] text-white">
                      <th className="text-left px-4 py-3 font-semibold">Elselskab</th>
                      <th className="text-right px-4 py-3 font-semibold">Tillæg</th>
                      <th className="text-right px-4 py-3 font-semibold">Abonnement</th>
                      <th className="text-right px-4 py-3 font-semibold">Årlig pris</th>
                      <th className="text-right px-4 py-3 font-semibold">Du sparer</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-[#D4A62A]/10 border-b border-gray-100">
                      <td className="px-4 py-3 font-bold text-[#1F2E33]">enkel energi</td>
                      <td className="text-right px-4 py-3 font-semibold text-[#1F2E33]">0 øre/kWh</td>
                      <td className="text-right px-4 py-3">18 kr./md.</td>
                      <td className="text-right px-4 py-3 font-bold text-[#1F2E33]">{fmt(enkelCost)} kr.</td>
                      <td className="text-right px-4 py-3">–</td>
                    </tr>
                    {comps.map((c, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-900">{c.name}</span>
                          {c.productName && <span className="block text-xs text-gray-500">{c.productName}</span>}
                        </td>
                        <td className="text-right px-4 py-3 text-gray-600">{c.surchargeOre > 0 ? `${c.surchargeOre} øre/kWh` : '0 øre/kWh'}</td>
                        <td className="text-right px-4 py-3 text-gray-600">{c.monthlyFee > 0 ? `${c.monthlyFee} kr./md.` : '0 kr./md.'}</td>
                        <td className="text-right px-4 py-3 font-semibold text-gray-900">{fmt(c.yearlyCost)} kr.</td>
                        <td className="text-right px-4 py-3">{c.savings > 0 && <span className="text-[#b23b30] font-semibold">+{fmt(c.savings)} kr.</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Priser inkl. moms ved {fmt(consumption)} kWh/år, {totalCompanies}+ selskaber sammenlignet. Kilde: Strømligning.dk. Opdateret {dateStr}. Tidsbegrænsede intro-tilbud er ekskluderet. Vejledende – se din egen pris på{' '}
                <a href="https://stromligning.dk" target="_blank" rel="noopener noreferrer" className="underline">Strømligning.dk</a>.
              </p>
            </>
          ) : (
            <div className="bg-[#F6F3EC] rounded-2xl p-6 border border-[#263A43]/10 text-center">
              <p className="text-gray-700">
                Hos enkel energi betaler du <strong>spotpris + 0 øre tillæg</strong> og abonnement fra <strong>18 kr./md.</strong> – det er typisk {fmt(enkelCost)} kr./år i selskabsomkostninger ved {fmt(consumption)} kWh/år.
              </p>
              <Link to="/elpris-sammenligning" className="inline-block mt-3 text-[#2F424D] font-semibold hover:text-[#D4A62A]">
                Se live sammenligning af alle elselskaber →
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}